import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './OrderDetails.module.scss';
import { Button, Icon, Input } from '@src/components/ui';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { getCart } from '../../utils/cartStorage';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveClinic } from '../../hooks/useEffectiveClinic';
import { useNavigate } from 'react-router-dom';
import { createOrder, calculateTax, getShippingMethods, confirmStripe3DSPayment } from '../../api/OrderApi';
import { normalizeShippingBreakdown } from '../../utils/shipping';
import { ShippingBreakdown } from '../../components/ShippingBreakdown';
import request, { requestError } from '../../utils/request';
import ROUTE_PATHS from '../../routes/paths';
import { validatePromoCode, } from '../../api/PromoCodeApi';
import { getPaymentMethods, createPaymentMethod, getStripeConfig, } from '../../api/PaymentMethodApi';
import { formatCurrency } from '../../utils/formatters';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// Card images - import directly
import visaCard from '../../assets/icons/cards/visa.png';
import mastercardCard from '../../assets/icons/cards/mastercard.png';
import amexCard from '../../assets/icons/cards/amex.png';
import discoverCard from '../../assets/icons/cards/discover.png';
import { useCart } from '@src/contexts/CartContext';
const StripeCardSetupForm = ({ loading, onCreatePaymentMethod, onError, }) => {
    const stripe = useStripe();
    const elements = useElements();
    const handleSave = async () => {
        if (!stripe || !elements) {
            onError('Stripe is still loading. Please try again.');
            return;
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            onError('Card input is not ready. Please try again.');
            return;
        }
        const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        });
        if (result.error || !result.paymentMethod) {
            onError(result.error?.message || 'Unable to tokenize card details. Please try again.');
            return;
        }
        const card = result.paymentMethod.card;
        await onCreatePaymentMethod({
            paymentMethodId: result.paymentMethod.id,
            brand: card?.brand || 'card',
            last4: card?.last4 || '0000',
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                    padding: '12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    background: '#fff',
                    marginBottom: '12px',
                }, children: _jsx(CardElement, { options: {
                        hidePostalCode: true,
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#1f2937',
                                '::placeholder': { color: '#9ca3af' },
                            },
                            invalid: {
                                color: '#dc2626',
                            },
                        },
                    } }) }), _jsx(Button, { label: loading ? 'Saving...' : 'Save Card', variant: "primary", shape: 'round', size: "small", customWidth: '98px', onClick: handleSave, disabled: loading || !stripe })] }));
};
const OrderDetails = ({ orderNumber = 98765 }) => {
    const navigate = useNavigate();
    const { isTablet, isMobile, isDesktop } = useBreakpoint();
    const isSmall = isTablet || isMobile;
    const { user, isAuthenticated, isCheckoutAuthorized } = useAuth();
    const { clinic, isImpersonating } = useEffectiveClinic();
    const { clearAll: clearCart } = useCart();
    const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
    const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
    const [paymentMethodsError, setPaymentMethodsError] = useState('');
    const [showAddPaymentSuccess, setShowAddPaymentSuccess] = useState(false);
    const [addPaymentSuccessMessage, setAddPaymentSuccessMessage] = useState('Card Saved!');
    const [paymentGatewayProvider, setPaymentGatewayProvider] = useState('paymentnow');
    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [stripeConfigLoading, setStripeConfigLoading] = useState(false);
    const [stripeConfigError, setStripeConfigError] = useState('');
    const [defaultShippingMethod, setDefaultShippingMethod] = useState(null);
    const [isLoadingDefaultShippingMethod, setIsLoadingDefaultShippingMethod] = useState(true);
    // Payment type state - new for credit feature
    const [paymentType, setPaymentType] = useState('credit_card');
    // Add payment method form state
    const [addPaymentForm, setAddPaymentForm] = useState({
        card_number: '',
        exp_month: '',
        exp_year: '',
        cvv: '',
    });
    const [addPaymentLoading, setAddPaymentLoading] = useState(false);
    const [addPaymentError, setAddPaymentError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderError, setOrderError] = useState('');
    const [orderStatus, setOrderStatus] = useState('idle');
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [orderResponse, setOrderResponse] = useState(null);
    // Tax calculation state
    const [taxData, setTaxData] = useState(null);
    const [taxLoading, setTaxLoading] = useState(false);
    const [taxError, setTaxError] = useState(null);
    // Promo code state
    const [promoCode, setPromoCode] = useState('');
    const [promoCodeApplied, setPromoCodeApplied] = useState(null);
    const [appliedPromoCodeText, setAppliedPromoCodeText] = useState('');
    const [promoCodeError, setPromoCodeError] = useState('');
    const [promoCodeLoading, setPromoCodeLoading] = useState(false);
    // Corporate/Clinic discount state
    const [corporateClinicDiscount, setCorporateClinicDiscount] = useState(null);
    const [discountLoading, setDiscountLoading] = useState(false);
    const stripePromise = useMemo(() => (stripePublishableKey ? loadStripe(stripePublishableKey) : null), [stripePublishableKey]);
    // Redirect on-hold clinics away from checkout
    useEffect(() => {
        if (isAuthenticated && isCheckoutAuthorized === false) {
            navigate('/app/shoppingCart');
        }
    }, [isAuthenticated, isCheckoutAuthorized, navigate]);
    // Cart state that updates when cart changes
    const [products, setProducts] = useState([]);
    useEffect(() => {
        const loadCartData = async () => {
            try {
                const cartData = await getCart(isAuthenticated);
                setProducts(cartData);
            }
            catch (err) {
                console.error('Failed to load cart:', err);
                setProducts([]);
            }
        };
        loadCartData();
    }, [isAuthenticated]);
    // Load saved payment methods when component mounts
    useEffect(() => {
        if (isAuthenticated) {
            loadSavedPaymentMethods();
            loadStripeFrontendConfig();
        }
        handleLoadDefaultShippingMethod();
    }, [isAuthenticated]);
    const loadStripeFrontendConfig = async () => {
        try {
            setStripeConfigLoading(true);
            setStripeConfigError('');
            const response = await getStripeConfig();
            const enabled = response?.body?.enabled;
            const publishableKey = response?.body?.publishableKey || '';
            const gatewayProvider = response?.body?.paymentGatewayProvider || response?.body?.payment_gateway_provider || 'paymentnow';
            setPaymentGatewayProvider(String(gatewayProvider).toLowerCase());
            if (enabled && publishableKey) {
                setStripePublishableKey(publishableKey);
            }
            else {
                setStripePublishableKey('');
            }
        }
        catch (error) {
            console.error('Error loading Stripe frontend config:', error);
            setStripeConfigError('Unable to initialize Stripe card setup.');
            setStripePublishableKey('');
            setPaymentGatewayProvider('paymentnow');
        }
        finally {
            setStripeConfigLoading(false);
        }
    };
    const handleLoadDefaultShippingMethod = async () => {
        setIsLoadingDefaultShippingMethod(true);
        try {
            const response = await getShippingMethods({ is_default: true });
            const firstDefaultShippingMethod = response.body[0];
            if (firstDefaultShippingMethod) {
                setDefaultShippingMethod(firstDefaultShippingMethod);
            }
        }
        catch (err) {
            console.error('Failed to load default shipping method:', err);
        }
        finally {
            setIsLoadingDefaultShippingMethod(false);
        }
    };
    const loadDiscountInfo = useCallback(async () => {
        console.log('💰 loadDiscountInfo called with:', {
            isAuthenticated,
            clinic,
            products,
            isImpersonating,
        });
        if (!isAuthenticated || !clinic || products.length === 0) {
            console.log('❌ Discount loading skipped: missing auth, clinic, or products');
            setCorporateClinicDiscount(null);
            return;
        }
        try {
            setDiscountLoading(true);
            const cartItems = products.map((product) => ({
                sku: product.sku,
                quantity: product.quantity,
                unit_price: product.price.toString(),
            }));
            const requestData = {
                cart_items: cartItems,
            };
            // If impersonating, include the clinic_id so backend knows which clinic to use for discounts
            if (isImpersonating && clinic?.id) {
                requestData.clinic_id = clinic.id;
                console.log('💰 Adding clinic_id for impersonation:', clinic.id);
            }
            console.log('💰 Discount request data:', requestData);
            const response = await request()
                .post('/api/v1/promo-codes/get-discount-info/')
                .send(requestData);
            if (response.body) {
                setCorporateClinicDiscount(response.body);
                // Debug logging
                console.log('Discount Info Response:', response.body);
                console.log('Product Discount Eligibility:');
                Object.entries(response.body.productInfo || {}).forEach(([sku, info]) => {
                    console.log(`  ${sku}:`, info);
                });
            }
        }
        catch (error) {
            console.error('Error loading discount info:', error);
            setCorporateClinicDiscount(null);
        }
        finally {
            setDiscountLoading(false);
        }
    }, [isAuthenticated, clinic, products, isImpersonating]);
    // Load discount info when products change
    useEffect(() => {
        loadDiscountInfo();
    }, [loadDiscountInfo]);
    const loadSavedPaymentMethods = async (skipAutoSelection = false) => {
        try {
            setPaymentMethodsLoading(true);
            setPaymentMethodsError('');
            const response = await getPaymentMethods();
            if (response.body && response.body.results) {
                setSavedPaymentMethods(response.body.results);
                // Auto-select the default payment method (unless skipped)
                if (!skipAutoSelection) {
                    const defaultMethod = response.body.results.find((method) => method.isDefault);
                    if (defaultMethod) {
                        setSelectedPaymentMethodId(defaultMethod.id);
                    }
                    else if (response.body.results.length > 0) {
                        // If no default, select the first one
                        setSelectedPaymentMethodId(response.body.results[0].id);
                    }
                }
                return response.body.results; // Return the loaded methods
            }
            return []; // Return empty array if no results
        }
        catch (error) {
            console.error('Error loading payment methods:', error);
            setPaymentMethodsError('Failed to load payment methods');
            return []; // Return empty array on error
        }
        finally {
            setPaymentMethodsLoading(false);
        }
    };
    const getPaymentMethodNickname = (cardNumber) => {
        const digits = cardNumber.replace(/\D/g, '');
        const lastFour = digits.slice(-4);
        if (!lastFour) {
            return 'Card';
        }
        return `Card ending in ${lastFour}`;
    };
    const handleAddPaymentMethod = async () => {
        if (paymentGatewayProvider === 'stripe') {
            setAddPaymentError('Use Stripe secure card entry to save your card.');
            return;
        }
        if (!isPaymentFormComplete()) {
            setAddPaymentError('Please fill in all fields');
            return;
        }
        try {
            setAddPaymentLoading(true);
            setAddPaymentError('');
            const paymentData = {
                nickname: getPaymentMethodNickname(addPaymentForm.card_number),
                cardNumber: addPaymentForm.card_number,
                expMonth: parseInt(addPaymentForm.exp_month),
                expYear: parseInt(addPaymentForm.exp_year),
                cvv: addPaymentForm.cvv,
            };
            const response = await createPaymentMethod(paymentData);
            if (response.body) {
                const alreadyExists = Boolean(response.body.already_exists ?? response.body.alreadyExists);
                const successMessage = response.body.message
                    || (alreadyExists ? 'Card already saved' : 'Card saved');
                // Clear form and close input
                setAddPaymentForm({
                    card_number: '',
                    exp_month: '',
                    exp_year: '',
                    cvv: '',
                });
                setShowAddPaymentForm(false);
                setAddPaymentSuccessMessage(successMessage);
                setShowAddPaymentSuccess(true);
                setTimeout(async () => {
                    await loadSavedPaymentMethods(true);
                    setShowAddPaymentSuccess(false);
                }, 3000);
            }
        }
        catch (error) {
            console.error('Error adding payment method:', error);
            const detail = error?.response?.body?.detail;
            setAddPaymentError(detail || 'Failed to add payment method. Please try again.');
        }
        finally {
            setAddPaymentLoading(false);
        }
    };
    const handleAddPaymentMethodTokenized = async ({ paymentMethodId, brand, last4, }) => {
        try {
            setAddPaymentLoading(true);
            setAddPaymentError('');
            const paymentData = {
                nickname: `${brand.toUpperCase()} ending in ${last4}`,
                stripePaymentMethodId: paymentMethodId,
            };
            const response = await createPaymentMethod(paymentData);
            if (response.body) {
                const alreadyExists = Boolean(response.body.already_exists ?? response.body.alreadyExists);
                const successMessage = response.body.message
                    || (alreadyExists ? 'Card already saved' : 'Card saved');
                setShowAddPaymentForm(false);
                setAddPaymentSuccessMessage(successMessage);
                setShowAddPaymentSuccess(true);
                setTimeout(async () => {
                    await loadSavedPaymentMethods(true);
                    setShowAddPaymentSuccess(false);
                }, 3000);
            }
        }
        catch (error) {
            console.error('Error adding tokenized payment method:', error);
            const detail = error?.response?.body?.detail;
            setAddPaymentError(detail || 'Failed to save card. Please try again.');
        }
        finally {
            setAddPaymentLoading(false);
        }
    };
    // Listen for cart updates
    useEffect(() => {
        const updateCartData = async () => {
            try {
                const cartData = await getCart(isAuthenticated);
                setProducts(cartData);
            }
            catch (err) {
                console.error('Failed to update cart:', err);
            }
        };
        // Listen for storage changes and custom cart update events
        window.addEventListener('storage', updateCartData);
        window.addEventListener('cartUpdated', updateCartData);
        return () => {
            window.removeEventListener('storage', updateCartData);
            window.removeEventListener('cartUpdated', updateCartData);
        };
    }, [isAuthenticated]);
    // Default shipping method (from the DB)
    // TODO: Get this from the DB
    // TODO: Make this dynamic based on the clinic's shipping method
    // Calculate tax using TaxJar API
    const calculateTaxForOrder = useCallback(async () => {
        console.log('🧮 calculateTaxForOrder called with:', {
            isAuthenticated,
            clinic,
            isImpersonating,
            products,
        });
        if (!isAuthenticated || !clinic) {
            console.log('❌ Tax calculation failed: User not authenticated or no clinic available');
            setTaxError('User not authenticated or no clinic available');
            return;
        }
        // Don't calculate tax if cart is empty
        if (!products || products.length === 0) {
            setTaxData(null);
            setTaxError(null);
            setTaxLoading(false);
            return;
        }
        setTaxLoading(true);
        setTaxError(null);
        try {
            const subTotal = products.reduce((sum, p) => sum + p.price * p.quantity, 0);
            // Apply promo discount to amount before tax calculation
            const promoDiscount = promoCodeApplied?.discountAmount || 0;
            const corporateClinicDiscountAmount = corporateClinicDiscount?.discountAmount || 0;
            const taxableAmount = subTotal - promoDiscount - corporateClinicDiscountAmount;
            console.log('Tax Calculation Debug:');
            console.log('  - Subtotal:', subTotal);
            console.log('  - Promo Discount:', promoDiscount);
            console.log('  - Corporate/Clinic Discount:', corporateClinicDiscountAmount);
            console.log('  - Taxable Amount:', taxableAmount);
            console.log('  - Promo Code Applied:', promoCodeApplied);
            console.log('  - Corporate/Clinic Discount Applied:', corporateClinicDiscount);
            // Validate required address fields
            const address = clinic.address;
            console.log('🔍 OrderDetails - Full address object:', address);
            if (!address.country || !(address.postalCode || address.postal_code) || !address.state) {
                console.error('❌ Missing required address fields:', {
                    country: address.country,
                    postalCode: address.postalCode,
                    postal_code: address.postal_code,
                    state: address.state,
                });
                setTaxError('Invalid address data for tax calculation');
                return;
            }
            const taxRequest = {
                shipping_address: {
                    country: address.country,
                    zip: address.postalCode || address.postal_code,
                    state: address.state,
                    city: address.city,
                    street: address.street1 || address.street_1,
                },
                amount: taxableAmount, // Send subtotal minus promo discount
                shipping_amount: Number(defaultShippingMethod?.price || 0),
                items: products.map((p) => ({ product_id: p.id, quantity: p.quantity })),
                shipping_method_id: defaultShippingMethod?.id,
            };
            console.log('🧾 Tax request for OrderDetails:', taxRequest);
            console.log('🏥 Clinic address data:', clinic.address);
            const response = await calculateTax(taxRequest);
            console.log('Tax response received:', response.body);
            if (response.body.success) {
                const newTaxData = {
                    tax_amount: response.body.taxAmount || response.body.tax_amount,
                    tax_rate: response.body.taxRate || response.body.tax_rate,
                    subtotal: subTotal, // Keep original subtotal for display
                    shipping_amount: Number(response.body.shippingAmount ?? response.body.shipping_amount ?? 0),
                    shipping_breakdown: normalizeShippingBreakdown(response.body.shippingBreakdown ?? response.body.shipping_breakdown),
                    total: response.body.total, // Use backend's calculated total
                };
                console.log('Setting new tax data:', newTaxData);
                setTaxData(newTaxData);
            }
            else {
                throw new Error('Tax calculation failed');
            }
        }
        catch (error) {
            console.error('Error calculating tax:', error);
            setTaxError('Failed to calculate tax. Please try again.');
        }
        finally {
            setTaxLoading(false);
        }
    }, [
        products,
        clinic,
        isAuthenticated,
        promoCodeApplied,
        corporateClinicDiscount,
        defaultShippingMethod?.price,
        defaultShippingMethod?.id,
        isImpersonating,
    ]);
    // Recalculate tax when products, user, or promo code changes
    useEffect(() => {
        calculateTaxForOrder();
    }, [calculateTaxForOrder]);
    // Add useEffect to recalculate tax when promo code is applied/removed
    useEffect(() => {
        console.log('Promo code changed, recalculating taxes...', promoCodeApplied);
        calculateTaxForOrder();
    }, [promoCodeApplied, calculateTaxForOrder]);
    const subTotal = products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    const promoDiscount = promoCodeApplied?.discountAmount || 0;
    const corporateClinicDiscountAmount = corporateClinicDiscount?.discountAmount || 0;
    const tax = taxData?.tax_amount || 0;
    const total = taxData?.total ?? subTotal + tax - promoDiscount - corporateClinicDiscountAmount;
    const totalItems = products.reduce((sum, p) => sum + p.quantity, 0);
    const formatDate = () => {
        const now = new Date();
        return now.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        });
    };
    const renderProductRows = () => products.map((product) => {
        console.log('🔍 DEBUG: Rendering product:', product.name, 'notDiscountable:', product.notDiscountable);
        return (_jsxs("tr", { children: [_jsxs("td", { className: styles.productName, children: [_jsx("img", { src: product.image, alt: product.name, width: "100", height: "100", className: styles.productImg }), _jsxs("div", { children: [_jsx("p", { children: product.name }), _jsx("p", { children: product.sku })] })] }), _jsx("td", { children: formatCurrency(product.price) }), _jsx("td", { children: product.quantity }), _jsxs("td", { children: [formatCurrency(product.price * product.quantity), product.notDiscountable && (_jsx("div", { style: {
                                color: '#8b8da0',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                marginTop: '4px',
                            }, children: "Not eligible for discount" }))] })] }, product.sku));
    });
    const renderProductCards = () => products.map((product) => {
        console.log('🔍 DEBUG: Rendering product card:', product.name, 'notDiscountable:', product.notDiscountable);
        return (_jsxs("div", { className: styles.productCard, children: [_jsx("img", { src: product.image, alt: product.name }), _jsxs("div", { className: styles.productCardInfo, children: [_jsx("p", { children: product.name }), _jsx("p", { children: product.sku }), _jsxs("p", { children: ["Qty: ", product.quantity] }), _jsxs("p", { children: ["Price: ", formatCurrency(product.price)] }), _jsxs("p", { children: ["Total: ", formatCurrency(product.price * product.quantity), product.notDiscountable && (_jsx("div", { style: {
                                        color: '#8b8da0',
                                        fontSize: '12px',
                                        fontStyle: 'italic',
                                        marginTop: '4px',
                                    }, children: "Not eligible for discount" }))] })] })] }, product.sku));
    });
    const renderCardIcons = () => (_jsxs("div", { className: styles.cartPaymentTitleWrapperIcons, children: [_jsx("img", { src: visaCard, alt: "Visa", width: "32", height: "32" }), _jsx("img", { src: mastercardCard, alt: "Mastercard", width: "32", height: "32" }), _jsx("img", { src: amexCard, alt: "American Express", width: "32", height: "32" }), _jsx("img", { src: discoverCard, alt: "Discover", width: "32", height: "32" })] }));
    // Check if payment form is complete
    const isPaymentFormComplete = () => {
        if (paymentGatewayProvider === 'stripe') {
            return false;
        }
        return (addPaymentForm.card_number &&
            addPaymentForm.exp_month &&
            addPaymentForm.exp_year &&
            addPaymentForm.cvv);
    };
    // Check if credit limit would be exceeded
    const isCreditLimitExceeded = () => {
        if (paymentType !== 'credit' || !clinic || !isAuthenticated) {
            return false;
        }
        // Use clinic from useEffectiveClinic hook
        // Add safety checks for credit fields
        if (clinic.paymentTerms !== 'Y') {
            return false;
        }
        // Convert strings to numbers safely
        const creditLimit = parseFloat(clinic.creditLimit?.toString() || '0') || 0;
        const accountBalance = parseFloat(clinic.accountBalance?.toString() || '0') || 0;
        if (creditLimit <= 0) {
            return false;
        }
        const availableCredit = creditLimit - accountBalance;
        return total > availableCredit;
    };
    // Update canPlaceOrder to include credit limit check
    const canPlaceOrder = () => {
        if (!isAuthenticated || !products.length || !clinic) {
            return false;
        }
        // For credit card payments, require a saved payment method
        if (paymentType === 'credit_card') {
            return !!selectedPaymentMethodId;
        }
        // For credit payments, check if clinic has credit terms enabled and limit not exceeded
        if (paymentType === 'credit') {
            const hasValidCreditTerms = clinic?.paymentTerms === 'Y';
            return hasValidCreditTerms && !isCreditLimitExceeded();
        }
        return false;
    };
    const clearCheckoutFeedback = useCallback(() => {
        if (orderError || orderStatus === 'failed' || paymentStatus === 'failed') {
            setOrderError('');
            setOrderStatus('idle');
            setPaymentStatus('pending');
            setOrderResponse(null);
        }
    }, [orderError, orderStatus, paymentStatus]);
    const getSavedMethodCardLogo = (method) => {
        const cardType = String(method.cardType || '').toLowerCase();
        const displayText = `${method.displayName || ''} ${method.nickname || ''}`.toLowerCase();
        if (cardType.includes('visa') || displayText.includes('visa')) {
            return { src: visaCard, alt: 'Visa' };
        }
        if (cardType.includes('mastercard')
            || cardType.includes('master card')
            || displayText.includes('mastercard')
            || displayText.includes('master card')) {
            return { src: mastercardCard, alt: 'Mastercard' };
        }
        if (cardType.includes('amex') || cardType.includes('american express') || displayText.includes('amex')) {
            return { src: amexCard, alt: 'American Express' };
        }
        if (cardType.includes('discover') || displayText.includes('discover')) {
            return { src: discoverCard, alt: 'Discover' };
        }
        return null;
    };
    const renderSavedPaymentMethods = () => {
        if (paymentMethodsLoading) {
            return _jsx("div", { className: styles.paymentMethodsLoading, children: "Loading payment methods..." });
        }
        // if (paymentMethodsError) {
        //   return (
        //     <div className={styles.paymentMethodsError}>
        //       <p>{paymentMethodsError}</p>
        //       <Button
        //         variant="secondary"
        //         size="small"
        //         label="Retry"
        //         onClick={() => loadSavedPaymentMethods()}
        //       />
        //     </div>
        //   );
        // }
        if (!savedPaymentMethods.length) {
            return _jsx("div", { className: styles.paymentMethodsLoading, children: "No Saved Payment Methods" });
        }
        return savedPaymentMethods.map((method) => {
            const cardLogo = getSavedMethodCardLogo(method);
            return (_jsxs("label", { className: styles.paymentMethodOption, children: [_jsx("input", { type: "radio", name: "paymentMethod", value: method.id, checked: paymentType === 'credit_card' && selectedPaymentMethodId === method.id, onChange: () => {
                            clearCheckoutFeedback();
                            setPaymentType('credit_card');
                            setSelectedPaymentMethodId(method.id);
                        } }), cardLogo && (_jsx("img", { src: cardLogo.src, alt: cardLogo.alt, className: styles.savedMethodCardLogo, width: "28", height: "18" })), _jsxs("span", { className: styles.paymentMethodText, children: [method.displayName, method.isDefault && _jsx("span", { className: styles.defaultBadge, children: " (Default)" })] })] }, method.id));
        });
    };
    const renderPaymentForm = () => {
        const isStripeGateway = paymentGatewayProvider === 'stripe';
        const isStripeTokenizationReady = Boolean(stripePromise);
        return (_jsx("div", { className: styles.paymentFormContainer, children: _jsxs("div", { className: styles.paymentForm, children: [stripeConfigLoading ? (_jsx("div", { className: styles.paymentMethodsLoading, children: "Initializing secure card entry..." })) : isStripeTokenizationReady ? (_jsxs(_Fragment, { children: [_jsx(Elements, { stripe: stripePromise, children: _jsx(StripeCardSetupForm, { loading: addPaymentLoading, onCreatePaymentMethod: handleAddPaymentMethodTokenized, onError: setAddPaymentError }) }), _jsx("p", { className: styles.paymentMethodsLoading, children: "Card details are tokenized in Stripe before being sent to our server." })] })) : isStripeGateway ? (_jsx("div", { className: styles.paymentMethodsLoading, children: "Stripe secure card entry is not available right now. Please contact support." })) : (_jsxs(_Fragment, { children: [_jsx(Input, { label: "Card Number", className: styles.inputItem, width: "100%", value: addPaymentForm.card_number, onChange: (value) => {
                                    setAddPaymentForm((prev) => ({
                                        ...prev,
                                        card_number: value.replace(/\D/g, '').slice(0, 19),
                                    }));
                                    if (addPaymentError)
                                        setAddPaymentError('');
                                }, error: !!addPaymentError, placeholder: "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022" }), _jsx("div", { className: styles.cardDetailsRow, children: _jsxs("div", { className: styles.expiryContainer, children: [_jsx(Input, { label: "Expiration Date", className: styles.inputItem, value: addPaymentForm.exp_month && addPaymentForm.exp_year
                                                ? `${addPaymentForm.exp_month}/${addPaymentForm.exp_year}`
                                                : addPaymentForm.exp_month || '', onChange: (value) => {
                                                if (value === '') {
                                                    setAddPaymentForm((prev) => ({
                                                        ...prev,
                                                        exp_month: '',
                                                        exp_year: '',
                                                    }));
                                                    return;
                                                }
                                                const cleaned = value.replace(/[^\d]/g, '');
                                                if (cleaned.length === 0) {
                                                    setAddPaymentForm((prev) => ({
                                                        ...prev,
                                                        exp_month: '',
                                                        exp_year: '',
                                                    }));
                                                }
                                                else if (cleaned.length === 1) {
                                                    setAddPaymentForm((prev) => ({
                                                        ...prev,
                                                        exp_month: cleaned,
                                                        exp_year: '',
                                                    }));
                                                }
                                                else if (cleaned.length === 2) {
                                                    setAddPaymentForm((prev) => ({
                                                        ...prev,
                                                        exp_month: cleaned,
                                                        exp_year: '',
                                                    }));
                                                }
                                                else if (cleaned.length <= 6) {
                                                    const month = cleaned.substring(0, 2);
                                                    const year = cleaned.substring(2, 6);
                                                    setAddPaymentForm((prev) => ({
                                                        ...prev,
                                                        exp_month: month,
                                                        exp_year: year,
                                                    }));
                                                }
                                            }, error: !!addPaymentError, placeholder: "MM/YYYY" }), _jsx(Input, { label: "CVV", className: styles.inputItem, value: addPaymentForm.cvv, onChange: (value) => {
                                                setAddPaymentForm((prev) => ({ ...prev, cvv: value.replace(/\D/g, '').slice(0, 4) }));
                                                if (addPaymentError)
                                                    setAddPaymentError('');
                                            }, error: !!addPaymentError, placeholder: "123" })] }) }), _jsx(Button, { label: addPaymentLoading ? 'Saving...' : 'Save Card', variant: "primary", shape: 'round', size: "small", customWidth: '98px', className: styles.saveCardButton, onClick: handleAddPaymentMethod, disabled: addPaymentLoading || !isPaymentFormComplete() }), _jsx("p", { className: styles.paymentMethodsLoading, children: "Stripe frontend tokenization is unavailable, using compatibility card entry path." })] })), stripeConfigError && _jsx("p", { className: styles.error, children: stripeConfigError }), addPaymentError && _jsx("p", { className: styles.error, children: addPaymentError })] }) }));
    };
    const renderAccountInfo = () => {
        console.log('renderAccountInfo :: user = ', user);
        if (!isAuthenticated || !user) {
            console.log('No user found or not authenticated');
            return null;
        }
        // Use clinic from useEffectiveClinic hook
        console.log('renderAccountInfo :: clinic = ', clinic);
        if (!clinic) {
            console.log('No clinic found');
            return null;
        }
        return (_jsxs("div", { className: styles.cartAccountInfo, children: [_jsxs("div", { children: [_jsx("h4", { children: "Account Details" }), _jsx("p", { children: clinic.name }), _jsxs("p", { children: ["Clinic Account Number: ", clinic.accountNumber || clinic.id] })] }), _jsxs("div", { children: [_jsx("h4", { children: "Shipping Address" }), _jsx("p", { children: clinic.address?.street1 }), clinic.address?.street2 && _jsx("p", { children: clinic.address.street2 }), _jsxs("p", { children: [clinic.address?.city, ", ", clinic.address?.state, " ", clinic.address?.postalCode] }), _jsx("p", { children: clinic.address?.country })] }), _jsxs("div", { children: [_jsx("h4", { children: "Contact Info" }), _jsx("p", { children: clinic.email }), _jsx("p", { children: clinic.phone?.number })] })] }));
    };
    const handlePlaceOrder = async () => {
        clearCheckoutFeedback();
        if (!isAuthenticated) {
            setOrderError('Please log in to place an order');
            return;
        }
        if (!clinic) {
            setOrderError('Please set up your clinic information to place an order');
            return;
        }
        if (!products.length) {
            setOrderError('Your cart is empty');
            return;
        }
        // Validate payment method based on payment type
        if (paymentType === 'credit_card' && !selectedPaymentMethodId) {
            setOrderError('Please select a payment method to continue');
            return;
        }
        if (paymentType === 'credit' && clinic?.paymentTerms !== 'Y') {
            setOrderError('Credit terms are not available for your account');
            return;
        }
        const shippingPostalCode = clinic.address?.postalCode ?? clinic.address?.postal_code ?? '';
        const contactPhoneNumber = clinic.phone?.number ?? clinic.phone?.formatted_number ?? '';
        if (!shippingPostalCode) {
            setOrderError('Clinic shipping ZIP/postal code is required before placing an order');
            return;
        }
        if (!contactPhoneNumber) {
            setOrderError('Clinic phone number is required before placing an order');
            return;
        }
        try {
            setIsSubmitting(true);
            setOrderError('');
            setOrderStatus('processing');
            setPaymentStatus('processing');
            // Use clinic from useEffectiveClinic hook
            // Debug logging
            console.log('Creating order with data:', {
                clinic: clinic.id,
                auth: {
                    isAuthenticated,
                    hasUser: !!user,
                    hasClinic: !!clinic,
                    csrfToken: document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('csrftoken='))
                        ?.split('=')[1],
                },
            });
            // Prepare order data with payment information
            const orderData = {
                clinic: clinic.id,
                shipping_address: {
                    street_1: clinic.address?.street1 ?? '',
                    street_2: clinic.address?.street2 ?? '',
                    city: clinic.address?.city ?? '',
                    state: clinic.address?.state ?? '',
                    postal_code: shippingPostalCode,
                    country: clinic.address?.country ?? '',
                },
                contact_email: clinic.email,
                contact_phone: {
                    number: contactPhoneNumber,
                },
                shipping_method: defaultShippingMethod?.id,
                items: products.map((product) => ({
                    product_id: product.id, // Link to the Product model
                    product_name: product.name,
                    sku: product.sku,
                    quantity: product.quantity,
                    unit_price: product.price,
                })),
                notes: '',
                // Add promo code if applied
                promo_code_text: appliedPromoCodeText ? appliedPromoCodeText.trim() : '',
                // Add saved payment method ID for processing
                saved_payment_method_id: paymentType === 'credit_card' ? selectedPaymentMethodId : undefined,
                payment_type: paymentType, // Add payment type to order data
            };
            console.log('CART DATA (frontend):', products);
            console.log('SUBTOTAL from cart:', subTotal);
            console.log('API REQUEST DATA:', orderData);
            console.log('Individual prices in API:', products.map((p) => ({ name: p.name, cartPrice: p.price, apiPrice: p.price })));
            console.log('Sending order request with data:', orderData);
            // Create the order (payment processing happens on backend)
            const response = await createOrder(orderData);
            const orderResponse = response.body;
            console.log('Order creation response:', orderResponse);
            console.log('Payment status from response:', orderResponse.paymentStatus);
            console.log('Payment status type:', typeof orderResponse.paymentStatus);
            console.log('Full order response keys:', Object.keys(orderResponse));
            console.log('Raw orderResponse object:', JSON.stringify(orderResponse, null, 2));
            console.log('Has paymentStatus property?', 'paymentStatus' in orderResponse);
            console.log('paymentStatus value directly:', orderResponse['paymentStatus']);
            // Check payment status from backend response
            const paymentStatusValue = orderResponse.paymentStatus || orderResponse.payment_status;
            const requiresAction = Boolean(orderResponse.requiresAction ?? orderResponse.requires_action);
            if (paymentStatusValue === 'completed') {
                setOrderStatus('success');
                setPaymentStatus('success');
                setOrderResponse(orderResponse);
                // Clear the cart only on successful payment
                await clearCart();
                // Clear payment form data for security
                setAddPaymentForm({
                    card_number: '',
                    exp_month: '',
                    exp_year: '',
                    cvv: '',
                });
                // Show success message and redirect
                setTimeout(() => {
                    navigate(ROUTE_PATHS.orderSuccess);
                }, 2000);
            }
            else if (requiresAction) {
                const stripe = await stripePromise;
                const clientSecret = orderResponse.paymentIntentClientSecret || orderResponse.payment_intent_client_secret;
                const paymentIntentId = orderResponse.paymentIntentId || orderResponse.payment_intent_id || orderResponse.paymentTransactionId || orderResponse.payment_transaction_id;
                const stripePaymentMethodId = orderResponse.stripePaymentMethodId || orderResponse.stripe_payment_method_id;
                if (!stripe || !clientSecret || !paymentIntentId || !orderResponse.id) {
                    setOrderStatus('success');
                    setPaymentStatus('failed');
                    setOrderResponse(orderResponse);
                    setOrderError('Payment requires additional authentication, but Stripe is not ready. Please retry checkout.');
                    return;
                }
                const confirmResult = stripePaymentMethodId
                    ? await stripe.confirmCardPayment(clientSecret, { payment_method: stripePaymentMethodId })
                    : await stripe.confirmCardPayment(clientSecret);
                if (confirmResult.error) {
                    setOrderStatus('success');
                    setPaymentStatus('failed');
                    setOrderResponse(orderResponse);
                    setOrderError(confirmResult.error.message || '3D Secure authentication failed. Please try again.');
                    return;
                }
                const confirmedStatus = confirmResult.paymentIntent?.status;
                if (confirmedStatus !== 'succeeded') {
                    setOrderStatus('success');
                    setPaymentStatus('failed');
                    setOrderResponse(orderResponse);
                    setOrderError(`3D Secure confirmation did not complete successfully (status: ${confirmedStatus || 'unknown'}).`);
                    return;
                }
                const confirmationResponse = await confirmStripe3DSPayment(orderResponse.id, paymentIntentId);
                const confirmationBody = confirmationResponse.body || {};
                const confirmedPaymentStatus = confirmationBody.paymentStatus || confirmationBody.payment_status;
                if (confirmedPaymentStatus === 'completed') {
                    setOrderStatus('success');
                    setPaymentStatus('success');
                    setOrderResponse({ ...orderResponse, ...confirmationBody });
                    await clearCart();
                    setAddPaymentForm({
                        card_number: '',
                        exp_month: '',
                        exp_year: '',
                        cvv: '',
                    });
                    setTimeout(() => {
                        navigate(ROUTE_PATHS.orderSuccess);
                    }, 2000);
                }
                else {
                    setOrderStatus('success');
                    setPaymentStatus('failed');
                    setOrderResponse({ ...orderResponse, ...confirmationBody });
                    setOrderError(confirmationBody.detail || 'Payment authentication did not complete. Please try again.');
                }
            }
            else {
                // Payment failed but order was created
                setOrderStatus('success');
                setPaymentStatus('failed');
                setOrderResponse(orderResponse);
            }
        }
        catch (error) {
            console.error('Error creating order:', error);
            // This is an order creation error
            setOrderStatus('failed');
            setPaymentStatus('failed');
            setOrderError('Failed to create order. Please try again.');
            // Log more details about the error
            if (error.response) {
                console.error('Error response:', {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data,
                    headers: error.response.headers,
                });
            }
            requestError(error, 'Failed to place order');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    // Promo code handlers
    const handlePromoCodeChange = (value) => {
        console.log('Promo code input changed:', value);
        setPromoCode(value);
        if (promoCodeError)
            setPromoCodeError('');
        // Clear applied promo when user changes the code
        if (promoCodeApplied) {
            console.log('Clearing applied promo code');
            setPromoCodeApplied(null);
            setAppliedPromoCodeText('');
        }
    };
    const handleApplyPromoCode = async () => {
        if (!promoCode.trim()) {
            setPromoCodeError('Please enter a promo code');
            return;
        }
        try {
            setPromoCodeLoading(true);
            setPromoCodeError('');
            console.log('Starting promo code validation for:', promoCode.trim());
            // Prepare cart items for validation
            const cartItems = products.map((product) => ({
                sku: product.sku,
                quantity: product.quantity,
                unit_price: product.price.toString(),
            }));
            console.log('Cart items:', cartItems);
            const validationRequest = {
                promo_code: promoCode.trim(),
                cart_items: cartItems,
                current_promo_code: appliedPromoCodeText || undefined,
            };
            const response = await validatePromoCode(validationRequest);
            const result = response.body;
            console.log('Promo validation response:', result);
            if (result.isValid) {
                console.log('Promo code validation successful:', result);
                setPromoCodeApplied(result);
                setAppliedPromoCodeText(promoCode.trim());
                setPromoCodeError('');
                // Tax will be recalculated automatically by useEffect
            }
            else {
                console.log('Promo code validation failed:', result);
                setPromoCodeError(result.message);
                // If the error is about a promo code already being applied, don't clear the current one
                // Otherwise, clear it (e.g., invalid promo code, expired, etc.)
                if (result.message && result.message.includes('already applied')) {
                    // Keep the current promo code - don't clear it
                    // The error message will inform the user they need to remove the current one first
                }
                else {
                    // For other errors (invalid code, expired, etc.), clear the promo code
                    setPromoCodeApplied(null);
                    setAppliedPromoCodeText('');
                }
            }
        }
        catch (error) {
            console.error('Error validating promo code:', error);
            setPromoCodeError('Failed to validate promo code. Please try again.');
            setPromoCodeApplied(null);
            setAppliedPromoCodeText('');
        }
        finally {
            setPromoCodeLoading(false);
        }
    };
    const isShippingLoading = taxLoading || isLoadingDefaultShippingMethod;
    return (_jsxs("main", { className: styles.cart, children: [_jsx("h2", { className: styles.title, children: "Checkout" }), _jsx("section", { className: styles.cartMain, children: _jsxs("section", { className: styles.cartWrapper, children: [_jsxs("div", { className: styles.cartOrderHeading, children: [_jsxs("h3", { children: ["Order ", orderNumber, " ", _jsx("span", { children: " | Digital Invoice" })] }), _jsxs("p", { children: ["Order Date: ", formatDate()] })] }), _jsx("div", { className: styles.cartDivider }), renderAccountInfo(), _jsx("div", { className: styles.cartAccountDetailsDivider }), isDesktop && (_jsxs("table", { cellPadding: "8", cellSpacing: "0", className: styles.cartProductTable, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Product" }), _jsx("th", { children: "Price" }), _jsx("th", { children: "Qty" }), _jsx("th", { children: "Total Price" })] }) }), _jsx("tbody", { children: renderProductRows() })] })), isSmall && _jsx("div", { className: styles.productCardView, children: renderProductCards() }), _jsx("hr", {}), _jsxs("div", { className: styles.cartPriceDetails, children: [_jsxs("p", { "data-testid": "summary-subtotal", children: ["Subtotal: ", formatCurrency(taxData?.subtotal || subTotal)] }), _jsx(ShippingBreakdown, { variant: "inline", entries: taxData?.shipping_breakdown ?? [], total: taxData?.shipping_amount ?? 0, loading: isShippingLoading, label: "Shipping:", suffix: defaultShippingMethod ? `(${defaultShippingMethod.estimatedDays} day delivery)` : undefined }), promoDiscount > 0 && (_jsxs("p", { style: { color: '#4caf50' }, children: ["Promo Discount: -", formatCurrency(promoDiscount)] })), _jsxs("p", { "data-testid": "summary-tax", children: ["Tax: ", taxLoading ? 'Calculating...' : formatCurrency(tax)] }), taxError && _jsx("p", { style: { color: '#d32f2f', fontSize: '12px' }, children: taxError }), _jsxs("h2", { "data-testid": "summary-total", children: ["Order Total: ", formatCurrency(total)] })] })] }) }), _jsxs("section", { className: styles.cartOrderDetails, children: [isDesktop && _jsx("h2", { children: "Order Summary" }), _jsxs("p", { className: styles.priceTitle, children: [formatCurrency(total), " Total"] }), _jsxs("p", { className: styles.priceItemList, children: [totalItems, " Items"] }), isSmall && isAuthenticated && clinic && (_jsxs("div", { className: styles.mobileShippingSection, children: [_jsx("h4", { children: "Shipping Address" }), _jsx("p", { children: clinic?.address.street1 }), clinic?.address.street2 && _jsx("p", { children: clinic.address.street2 }), _jsxs("p", { children: [clinic?.address.city, ", ", clinic?.address.state, " ", clinic?.address.postalCode] })] })), _jsx("div", { className: styles.cartDivider }), _jsxs("div", { children: [_jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsxs("p", { children: ["Total Items ", _jsxs("span", { children: ["(", totalItems, ")"] })] }), _jsx("p", { children: formatCurrency(taxData?.subtotal || subTotal) })] }), _jsx("div", { className: `${styles.cartOrderDetailsItems} ${styles.shippingRow}`, children: _jsx(ShippingBreakdown, { entries: taxData?.shipping_breakdown ?? [], total: taxData?.shipping_amount ?? 0, loading: isShippingLoading, label: "Shipping" }) }), corporateClinicDiscount?.hasDiscount && (_jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsxs("p", { children: [corporateClinicDiscount.discountSourceDisplay, " (", corporateClinicDiscount.discountPercentage, "%)"] }), _jsxs("p", { style: { color: '#4caf50' }, children: ["-", formatCurrency(corporateClinicDiscount.discountAmount)] })] })), promoDiscount > 0 && (_jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsx("p", { children: "Promo Discount" }), _jsxs("p", { style: { color: '#4caf50' }, children: ["-", formatCurrency(promoDiscount)] })] })), _jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsx("p", { children: "Taxes" }), taxLoading ? _jsx("p", { children: "Calculating..." }) : _jsx("p", { children: formatCurrency(tax) })] }), taxError && (_jsx("div", { className: styles.cartOrderDetailsItems, children: _jsx("p", { style: { color: '#d32f2f', fontSize: '12px' }, children: taxError }) })), _jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsx("p", { children: "Order Total" }), _jsx("p", { children: formatCurrency(total) })] })] }), _jsx("div", { className: styles.cartDivider }), _jsxs("div", { className: styles.discountCodeSection, children: [_jsx("h4", { children: "Add Promo Code" }), _jsx(Input, { className: styles.inputItem, value: promoCode, onChange: handlePromoCodeChange, placeholder: "Enter promo code (e.g., ENDURANCE35)", width: "100%", error: !!promoCodeError && !promoCodeApplied }), promoCodeError && _jsx("p", { className: styles.error, children: promoCodeError }), promoCodeApplied && (_jsxs("div", { className: styles.promoCodeSuccess, children: [_jsx("p", { className: styles.success, style: {
                                            color: '#4caf50 !important',
                                            fontWeight: '500',
                                            background: 'transparent',
                                        }, children: promoCodeApplied.message }), _jsxs("p", { className: styles.discountAmount, children: ["Discount: -", formatCurrency(promoCodeApplied.discountAmount)] })] })), promoCodeApplied ? (_jsx(Button, { label: "Remove Promo", variant: "secondary", size: "small", onClick: () => {
                                    setPromoCodeApplied(null);
                                    setAppliedPromoCodeText('');
                                    setPromoCodeError('');
                                } })) : (_jsx(Button, { label: promoCodeLoading ? 'Validating...' : 'Apply Promo', variant: "secondary", size: "small", onClick: handleApplyPromoCode, disabled: !promoCode.trim() || promoCodeLoading }))] }), _jsxs("div", { className: styles.cartPaymentMethodsSection, children: [_jsxs("div", { className: styles.cartPaymentTitleWrapper, children: [_jsx("h4", { children: "Payment Methods" }), renderCardIcons()] }), isAuthenticated && (_jsxs("div", { className: styles.paymentMethodsList, children: [renderSavedPaymentMethods(), clinic?.paymentTerms === 'Y' && (_jsxs("label", { className: styles.paymentMethodOption, children: [_jsx("input", { type: "radio", name: "paymentMethod", value: "credit", checked: paymentType === 'credit', onChange: () => {
                                                    clearCheckoutFeedback();
                                                    setPaymentType('credit');
                                                } }), _jsxs("span", { className: styles.paymentMethodText, children: ["Credit Terms", ' ', clinic?.creditLimit && parseFloat(clinic.creditLimit.toString()) > 0 && (_jsxs("span", { className: styles.creditAvailableText, children: ["(Available Credit:", ' ', formatCurrency(parseFloat(clinic.creditLimit?.toString() || '0') -
                                                                parseFloat(clinic.accountBalance?.toString() || '0')), ")"] }))] })] })), _jsx("div", { className: styles.addPaymentMethodContainer, children: _jsxs("button", { type: "button", className: styles.addPaymentMethodButton, onClick: () => {
                                                clearCheckoutFeedback();
                                                setShowAddPaymentForm(!showAddPaymentForm);
                                                if (showAddPaymentForm) {
                                                    // Clear form when canceling
                                                    setAddPaymentForm({
                                                        card_number: '',
                                                        exp_month: '',
                                                        exp_year: '',
                                                        cvv: '',
                                                    });
                                                    setAddPaymentError('');
                                                }
                                            }, children: [_jsx("span", { style: { textDecoration: 'none' }, children: _jsx(Icon, { faIconName: showAddPaymentForm ? 'fas fa-times' : 'fas fa-plus', "aria-hidden": "true" }) }), showAddPaymentForm ? 'Cancel' : 'Add Payment Method'] }) }), showAddPaymentForm && _jsx("div", { style: { marginTop: '8px' }, children: renderPaymentForm() })] }))] }), paymentType === 'credit' && isAuthenticated && clinic?.paymentTerms === 'Y' && (_jsx("div", { className: styles.creditTermsInfoBox, children: isCreditLimitExceeded() ? (_jsxs("div", { className: styles.creditTermsError, children: [_jsx("p", { className: styles.errorTitle, children: "The order total exceeds your current credit limit." }), _jsxs("p", { className: styles.errorSubtext, children: ["Please choose another payment method or", ' ', _jsx("span", { className: styles.contactLink, children: "contact customer service" }), " to review your credit options."] }), _jsxs("div", { className: styles.creditLimitDetails, children: [_jsx("p", { children: _jsxs("strong", { children: ["Available Credit:", ' ', formatCurrency(parseFloat(clinic?.creditLimit?.toString() || '0') -
                                                        parseFloat(clinic?.accountBalance?.toString() || '0'))] }) }), _jsxs("p", { children: ["Credit Limit: ", formatCurrency(parseFloat(clinic?.creditLimit?.toString() || '0'))] })] })] })) : (_jsx("div", { className: styles.creditTermsSuccess, children: _jsx("p", { children: "This order will be charged to your account. Payment is due based on your account terms." }) })) })), orderStatus === 'processing' && (_jsx("div", { className: styles.statusMessage, children: _jsx("p", { children: "Creating your order..." }) })), orderStatus === 'success' && paymentStatus === 'processing' && (_jsx("div", { className: styles.statusMessage, children: _jsx("p", { children: "Processing payment..." }) })), orderStatus === 'success' && paymentStatus === 'success' && (_jsx("div", { className: styles.successMessage, children: _jsx("p", { children: "Payment processed successfully!" }) })), orderStatus === 'success' && paymentStatus === 'failed' && (_jsxs("div", { className: styles.errorMessage, children: [_jsx("p", { children: "Payment processing failed" }), _jsx("p", { children: "Please try again" })] })), orderError && (_jsx("div", { className: styles.errorMessage, children: _jsx("p", { children: orderError }) })), isAuthenticated && products.length > 0 && !clinic && (_jsx("div", { className: styles.errorMessage, children: _jsx("p", { children: "Please set up your clinic information to place your order" }) })), showAddPaymentSuccess && (_jsx("div", { className: styles.successMessage, children: _jsx("p", { children: addPaymentSuccessMessage }) })), savedPaymentMethods.length > 0 && !showAddPaymentForm && (_jsx("div", { className: styles.cardIconsAboveButton, style: {
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginBottom: '36px',
                        }, children: renderCardIcons() })), _jsx(Button, { label: isSubmitting ? 'Processing...' : 'Place My Order', variant: "checkout", onClick: handlePlaceOrder, disabled: isSubmitting || !canPlaceOrder() || showAddPaymentForm })] })] }));
};
export default OrderDetails;
